import * as THREE from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { Base } from "../utils/base";
import { Database } from "./database";
import { colors, fonts } from "./colors";
import { createCubeBox, setColor, createTexture, resetCube } from "./cube";
import { createTube, updateTube } from "./tube";
import { addTextObject, updateTextObject } from "./text";
import { data } from "./demo";
import { BoundingBoxManager } from "./bounding_box";
import { Gizmo } from "./gizmo";
import { getIntersectPoint } from "./core";

export class Board extends Base {
  constructor(container) {
    super({ container });
    this.connections = new Map();
    this.colors = colors;
    this.editingText = null;
    this.editingLabel = false;
    this.cursorVisible = false;
    this.baseText = "";
    this.cursorCounter = 0;
    this.database = new Database();
    this.currentAnimationIndex = 0;
    this.flowAnimation = {
      animating: false,
      index: 0,
      allAnimations: [],
      currentAnimations: [], //current animation
      onComplete: () => {},
      shouldReset: true,
    };
    this.bboxManager = new BoundingBoxManager(
      this.scene,
      this.renderer,
      this.camera,
    );

    this.gizmo = new Gizmo(
      this.scene,
      this.camera,
      this.raycaster,
      this.renderer,
    );
  }

  startRenderLoop() {
    this.renderer.setAnimationLoop(() => {
      this.orbitControls.update();

      if (this.flowAnimation.animating) {
        this.updateFlowAnimation();
      }

      // Blinking cursor during editing
      if (this.editingText) {
        this.cursorCounter = (this.cursorCounter || 0) + 1;
        if (this.cursorCounter % 30 === 0) {
          this.updateEditingText();
        }
      }

      this.renderer.render(this.scene, this.camera);
      this.CSS2DRenderer.render(this.scene, this.camera);
    });
  }

  stopRenderLoop() {
    this.renderer.setAnimationLoop(null);
  }

  getBoundingBoxCorners(object) {
    const topRight = this.bboxManager.getTopRightScreenPosition(object);

    return {
      topRight,
    };
  }

  updateFlowAnimation() {
    const currentAnimation =
      this.flowAnimation.currentAnimations[this.flowAnimation.index];

    if (currentAnimation) {
      const { sphere, curve } = currentAnimation;

      const position = curve.getPoint(currentAnimation.counter);

      sphere.position.set(position.x, 0.09, position.z);

      currentAnimation.counter += 0.01; // Set outside the loop

      if (currentAnimation.counter >= 1) {
        this.deleteObject(sphere);
        const { target } = currentAnimation;

        updateTextObject(target, currentAnimation.destination.text);
        updateTextObject(target, currentAnimation.destination.text, "back");
        updateTextObject(target, currentAnimation.destination.text, "left");
        updateTextObject(target, currentAnimation.destination.text, "right");
        setColor(target, currentAnimation.destination.color);

        this.flowAnimation.index++;
      }
    } else {
      if (this.flowAnimation.shouldReset) {
        this.resetAnimation();
      }
      this.flowAnimation.animating = false;
    }
  }

  setFontSize(object, value) {
    const fontSize = fonts[value];
    object.children.forEach((object) => {
      if (["front", "back", "left", "right", "top"].includes(object.name)) {
        object.fontSize = fontSize;
      }
    });
    this.database.saveCube(object);
  }

  deleteCube(cube) {
    this.database.deleteCube(cube);
  }

  saveCube(cube) {
    this.database.saveCube(cube);
  }

  saveTube(tube) {
    this.database.saveTube(tube);
  }

  createCube({
    sides = [
      { name: "front", value: "", fontSize: 0.1 },
      { name: "back", value: "", fontSize: 0.1 },
      { name: "left", value: "", fontSize: 0.1 },
      { name: "right", value: "", fontSize: 0.1 },
      { name: "top", value: "", fontSize: 0.1 },
    ],
  } = {}) {
    const cube = createCubeBox();
    sides.forEach((side) => {
      this.addText(cube, side.value, side.name, side.fontSize);
    });
    return cube;
  }

  addDemoTemplate = async () => {
    const persistedCubes = await this.database.getAllCubes();
    if (persistedCubes.length > 0) return;

    const cubes = data.cubes;
    cubes.forEach((cube) => {
      const mesh = this.createCube({ sides: cube.sides });
      mesh.uuid = cube.id;
      this.addLabel(mesh, cube.labelText);
      setColor(mesh, cube.color);
      mesh.position.fromArray(cube.position);
      mesh.rotation.fromArray(cube.rotation);
      mesh.scale.fromArray(cube.scale);
      this.scene.add(mesh);
      this.database.saveCube(mesh);
    });

    const tubeRecords = data.tubes;

    tubeRecords.forEach((tubeRecord) => {
      const firstObject = this.scene.getObjectByProperty(
        "uuid",
        tubeRecord.firstObjectUuid,
      );
      const secondObject = this.scene.getObjectByProperty(
        "uuid",
        tubeRecord.secondObjectUuid,
      );
      const tube = this.connectObjects(firstObject, secondObject);
      tube.uuid = tubeRecord.id;
      tube.userData.animations = tubeRecord.animations;

      this.database.saveTube(tube);
    });
  };

  async loadScene() {
    const cubes = await this.database.getAllCubes();
    cubes.forEach((cube) => {
      const mesh = this.createCube({ sides: cube.sides });
      mesh.uuid = cube.id;
      this.addLabel(mesh, cube.labelText);
      setColor(mesh, cube.color);
      mesh.position.fromArray(cube.position);
      mesh.rotation.fromArray(cube.rotation);
      mesh.scale.fromArray(cube.scale);

      this.scene.add(mesh);
    });

    const tubeRecords = await this.database.getAllTubes();

    tubeRecords.forEach((tubeRecord) => {
      const firstObject = this.scene.getObjectByProperty(
        "uuid",
        tubeRecord.firstObjectUuid,
      );
      const secondObject = this.scene.getObjectByProperty(
        "uuid",
        tubeRecord.secondObjectUuid,
      );
      const tube = this.connectObjects(firstObject, secondObject);
      tube.uuid = tubeRecord.id;

      tube.userData.animations = tubeRecord.animations;
    });
  }

  devMode() {
    //const normal = new THREE.Vector3(0, 1, 0); // XZ directions
    //const normal = new THREE.Vector3(1, 0, 0); // YZ directions
    const normal = new THREE.Vector3(0, 0, 1); // XY directions
    const constant = 0; // distance from origin
    const groundPlane = new THREE.Plane(normal, constant);
    const planeHelper = new THREE.PlaneHelper(groundPlane, 10, 0xff0000); // 10 = size, red color
    this.scene.add(planeHelper);
  }

  setup() {
    //this.devMode();

    this.createHiddenInput();

    const platform = this.createPlatform();
    this.scene.add(platform);

    this.startRenderLoop();
  }

  addAnimation(tube, firstObject, secondObject) {
    const flow = {
      fromUuid: firstObject.uuid,
      toUuid: secondObject.uuid,
      type: "pulse",
      orderNumber: 1,
      origin: { text: "GET", color: "yellow" },
      destination: { text: "OK", color: "green" },
    };
    tube.userData.animations.push(flow);
  }

  updateAnimation(tube, text, color, orderNumber) {
    const flow = tube.userData.animations[0];
    flow.destination.text = text;
    flow.destination.color = color;
    flow.orderNumber = orderNumber;
  }

  createActiveAnimations = (templateAnimations) => {
    return templateAnimations.map(({ fromUuid, toUuid, ...rest }) => {
      const source = this.scene.getObjectByProperty("uuid", fromUuid);
      const target = this.scene.getObjectByProperty("uuid", toUuid);

      const sphere = this.createSphere();
      sphere.position.setY(-0.16);
      this.scene.add(sphere);

      const curve = new THREE.CatmullRomCurve3([
        source.position,
        target.position,
      ]);

      return {
        ...rest,
        source,
        target,
        sphere,
        curve,
        counter: 0.0,
      };
    });
  };

  async getActiveAnimations() {
    const tubes = await this.database.getAllTubes();
    if (tubes.length === 0) return;

    const templateAnimations = tubes.flatMap((tube) => tube.animations);
    const activeAnimations = this.createActiveAnimations(templateAnimations);
    const sortedActiveAnimations = activeAnimations.sort(
      (a, b) => a.orderNumber - b.orderNumber,
    );
    return sortedActiveAnimations;
  }

  async playNext({ onComplete = () => {} } = {}) {
    const allAnimations = await this.getActiveAnimations();
    if (allAnimations.length === 0) return;

    const lastIndex = allAnimations.length - 1;
    if (this.currentAnimationIndex > lastIndex) this.currentAnimationIndex = 0;

    const animation = allAnimations[this.currentAnimationIndex];

    this.currentAnimationIndex++;

    const shouldReset = this.currentAnimationIndex > lastIndex;

    this.flowAnimation = {
      animating: true,
      index: 0,
      allAnimations,
      currentAnimations: [animation],
      onComplete,
      shouldReset,
    };
  }

  async play({ onComplete = () => {} } = {}) {
    const allAnimations = await this.getActiveAnimations();
    if (allAnimations.length === 0) return;

    this.flowAnimation = {
      animating: true,
      index: 0,
      allAnimations,
      currentAnimations: allAnimations,
      onComplete,
      shouldReset: true,
    };
  }

  async resetCubes() {
    const cubeRecords = await this.database.getAllCubes();
    cubeRecords.forEach((cubeRecord) => {
      const cube = this.scene.getObjectByProperty("uuid", cubeRecord.id);

      const originalColor = cubeRecord.color;
      setColor(cube, originalColor);

      const originalSides = cubeRecord.sides;
      originalSides.forEach((side) => {
        updateTextObject(cube, side.value, side.name);
      });
    });
  }

  resetAnimation({ timeout = 1000 } = {}) {
    if (this.flowAnimation.allAnimations.length === 0) return;

    const cubes = this.flowAnimation.allAnimations.map(({ target }) => target);

    setTimeout(() => {
      this.resetCubes(cubes);
    }, timeout);
    this.flowAnimation.onComplete();
    this.flowAnimation.allAnimations.forEach(({ sphere }) => {
      this.deleteObject(sphere);
    });
    this.flowAnimation.animating = false;
  }

  startEditingText(intersect) {
    this.currentSide = this.determineSide(intersect.normal);
    const textObject = this.findTextObject(intersect.object, this.currentSide);

    const textInput = document.getElementById("hidden-input-like-hoodini");
    textInput.value = textObject?.text || "";
    textInput.focus();
    this.editingText = textObject;
    this.baseText = textObject.text;
  }

  createHiddenInput = () => {
    const input = document.createElement("input");
    input.id = "hidden-input-like-hoodini";
    input.type = "text";
    input.style.cssText =
      "position: fixed; opacity: 0; pointer-events: none; z-index: -10;";

    input.oninput = (event) => {
      this.addText(this.selectedObject, event.target.value, this.currentSide);
    };

    input.onblur = () => {
      this.editingText.text = this.baseText; //remove last cursor blink
      this.database.saveCube(this.selectedObject);
      this.editingText = null;
      this.baseText = "";
    };

    this.container.appendChild(input);
  };

  updateEditingText() {
    if (this.editingText) {
      this.editingText.text = this.cursorVisible
        ? this.baseText
        : this.baseText + "|";
      this.cursorVisible = !this.cursorVisible; // Toggle state
      this.editingText.sync();
    }
  }

  createPlatform() {
    const groundGeometry = new THREE.BoxGeometry(10, 0.5, 10);

    const texture = createTexture({
      color: "gray",
      lineColor: "dark",
      lineWidth: 1,
    });
    const groundMaterial = new THREE.MeshBasicMaterial({ map: texture });

    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.position.set(0, -0.25, 0);
    return ground;
  }

  getConnectedObjects(object) {
    const tubes = this.connections.get(object.uuid) || [];
    let objects = [];
    tubes.forEach((tube) => {
      objects.push(tube.userData.firstObject);
      objects.push(tube.userData.secondObject);
    });
    objects = objects.filter((obj) => obj.name != object.name);
    return objects;
  }

  createSphere() {
    const geometry = new THREE.SphereGeometry(0.06);
    const material = new THREE.MeshBasicMaterial({
      color: this.colors["yellow"],
    });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.name = "__sphere__";
    return sphere;
  }

  connectObjects(firstObject, secondObject) {
    const curvePoints = [
      firstObject.position.clone(),
      secondObject.position.clone(),
    ];

    const tube = createTube(curvePoints);
    tube.position.setY(-0.16);

    tube.userData = { firstObject, secondObject };
    tube.userData.animations = [];

    this.saveConnection(firstObject.uuid, tube);
    this.saveConnection(secondObject.uuid, tube);

    this.scene.add(tube);

    return tube;
  }

  addPreviewTube({ startPosition, endPosition } = {}) {
    this.orbitControls.enabled = false;
    const curvePoints = [startPosition, endPosition];
    const mesh = createTube(curvePoints);
    mesh.name = "preview_tube";
    this.scene.add(mesh);
  }

  updatePreviewTube({ startPosition, endPosition }) {
    const mesh = this.scene.getObjectByName("preview_tube");
    if (!mesh) {
      //TODO: fix this bug
      console.log("failed to update preview tube");
      return;
    }

    endPosition.y = 0.25;
    const curvePoints = [startPosition, endPosition];
    updateTube(mesh, curvePoints);
  }

  removePreviewTube() {
    this.orbitControls.enabled = true;

    const mesh = this.scene.getObjectByName("preview_tube");
    if (!mesh) return;
    this.deleteObject(mesh);
  }

  updateTubesPositions() {
    if (!this.selectedObject) return;

    const connections = this.connections.get(this.selectedObject.uuid) || [];
    connections.forEach((connection) => {
      this.updateConnection(connection);
    });
  }

  updateObjectPosition = (event) => {
    if (this.selectedObject) {
      const intersectPoint = getIntersectPoint(
        event,
        this.camera,
        this.raycaster,
        this.renderer,
      );
      intersectPoint.y = this.selectedObject.position.y;
      this.selectedObject.position.copy(intersectPoint);
      this.gizmo.update();

      const connections = this.connections.get(this.selectedObject.uuid) || [];
      connections.forEach((connection) => {
        this.updateConnection(connection);
      });
    }
  };

  updateConnection(mesh) {
    const { firstObject, secondObject } = mesh.userData;

    const curvePoints = [
      firstObject.position.clone(),
      secondObject.position.clone(),
    ];

    updateTube(mesh, curvePoints);
  }

  saveConnection(key, value) {
    if (this.connections.has(key)) {
      this.connections.get(key).push(value);
    } else {
      this.connections.set(key, [value]);
    }
  }

  deleteObject(object) {
    if (!object) return;
    this.deleteLabel(object);
    this.gizmo.remove();

    object.removeFromParent();
    object.geometry.dispose();
    object.material.dispose();

    const connections = this.connections.get(object.uuid) || [];
    connections.forEach((connection) => {
      connection.removeFromParent();
      connection.geometry.dispose();
      connection.material.dispose();
    });
  }

  resetObject(object) {
    resetCube(object);
  }

  scaleObject = (object, scaleFactor, axis) => {
    if (Number(scaleFactor) < 0.001) return; // Changed to 0.001 to prevent scale issues

    if (axis === "x") {
      object.scale.x = scaleFactor;
    } else if (axis === "y") {
      const heightBefore = this.getObjectHeight(object); //only for y axis
      object.scale.y = scaleFactor;
      const heightAfter = this.getObjectHeight(object);
      const heightChange = heightAfter - heightBefore;
      object.position.y += heightChange / 2;
    } else if (axis === "z") {
      object.scale.z = scaleFactor;
    } else {
      object.scale.set(scaleFactor, scaleFactor, scaleFactor);
    }
  };

  getObjectHeight(object) {
    const boundingBox = new THREE.Box3().setFromObject(object);
    return boundingBox.max.y - boundingBox.min.y;
  }

  getIntersectedObject(event) {
    const coords = new THREE.Vector2(
      (event.clientX / this.renderer.domElement.clientWidth) * 2 - 1,
      -(event.clientY / this.renderer.domElement.clientHeight) * 2 + 1,
    );
    this.raycaster.setFromCamera(coords, this.camera);

    const intersects = this.raycaster.intersectObjects(this.scene.children);
    return intersects.length > 0 ? intersects[0] : null;
  }

  setCubeColor(object, value) {
    setColor(object, value);
  }

  selectObject(object) {
    if (!object) return;
    this.bboxManager.add(object);
    this.orbitControls.enabled = false;
    this.selectedObject = object;
    this.gizmo.add(object);
  }

  deselectObject(object) {
    if (!object) return;

    this.bboxManager.remove(object);
    this.gizmo.remove(object);

    if (!this.selectedObject) return;
    this.selectedObject = null;
    this.orbitControls.enabled = true;
  }

  deleteLabel(object) {
    const label = object.getObjectByName("label");
    if (!label) return;
    if (label.element && label.element.parentNode) {
      label.element.remove();
    }
    object.remove(label);
  }

  addLabel(object, text = "") {
    const existingLabel = object.getObjectByName("label");
    if (existingLabel && existingLabel.element instanceof HTMLElement) {
      existingLabel.element.textContent = text;
      object.name = text;
    } else {
      const div = document.createElement("div");
      div.id = object.id;
      div.style.color = "black";
      div.style.fontSize = "16px";
      div.style.pointerEvents = "auto";
      div.style.cursor = "pointer";
      div.style.backgroundColor = "rgba(255, 255, 255, 0.7)";
      div.style.padding = "5px";
      div.style.borderRadius = "5px";

      const labelSpan = document.createElement("span");
      labelSpan.textContent = text;
      labelSpan.style.display = "inline-block"; // Visible by default

      const input = document.createElement("input");
      input.type = "text";
      input.value = text;
      input.style.display = "none";
      input.style.fontSize = "16px";
      input.style.padding = "4px 8px";
      input.style.border = "none";
      input.style.borderRadius = "2px";
      input.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";

      div.appendChild(labelSpan);
      div.appendChild(input);

      div.addEventListener("click", (e) => {
        this.editingLabel = true;

        e.stopPropagation();
        labelSpan.style.display = "none";
        input.style.display = "inline-block";
        input.focus();
      });

      input.addEventListener("blur", () => {
        this.editingLabel = false;

        labelSpan.textContent = input.value;
        object.name = input.value;
        this.database.saveCube(object);
        labelSpan.style.display = "inline-block";
        input.style.display = "none";
      });

      const label = new CSS2DObject(div);
      const y = this.getObjectHeight(object) / 2 + 0.2;
      label.position.setY(y);
      label.name = "label";
      object.name = text;
      object.add(label);
    }
  }

  determineSide(faceNormal) {
    if (faceNormal.z === 1) {
      return "front"; // Positive Z axis
    } else if (faceNormal.z === -1) {
      return "back"; // Negative Z axis
    } else if (faceNormal.x === 1) {
      return "right"; // Positive X axis
    } else if (faceNormal.x === -1) {
      return "left"; // Negative X axis
    } else if (faceNormal.y === 1) {
      return "top";
    }

    return null;
  }

  findTextObject(object, side) {
    return object.children.find((child) => child.name === side);
  }

  addText(object, value = "", side = "front", fontSize = 0.1) {
    addTextObject(object, value, side, fontSize);
    if (this.editingText) {
      this.baseText = value;
      this.updateEditingText(); // Immediately reflect with cursor
    }
  }

  rotateObject(object, degrees, axis = "y") {
    const radians = degrees * (Math.PI / 180);
    object.rotation[axis] = radians;
  }

  getIntersectPoint(event) {
    return getIntersectPoint(event, this.camera, this.raycaster, this.renderer);
  }

  insertShape(shape, event) {
    if (shape !== "cube") return;

    const intersectPoint = getIntersectPoint(
      event,
      this.camera,
      this.raycaster,
      this.renderer,
    );
    const cube = this.createCube();
    this.addLabel(cube, "Untitled");

    cube.position.copy(intersectPoint);
    cube.position.y += 0.25; // Slightly raise to make it sit on the ground level

    this.database.saveCube(cube);
    this.scene.add(cube);

    return cube;
  }
}
